import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export interface ControlQuestionSurvey {
  id: number;
  tenant_id: number;
  title: string;
  description?: string | null;
  intro_text?: string | null;
  outro_text?: string | null;
  status: string;
  intranet_enabled?: number | null;
  link_id?: number | null;
  link_token?: string | null;
  link_status?: string | null;
  link_expires_at?: string | null;
  created_by: number;
  created_at: string;
  updated_at?: string | null;
}

export interface ControlQuestionSurveyMetrics {
  survey_id: number;
  total_accesses: number;
  total_responses: number;
  total_abandonments: number;
  avg_completion_seconds: number;
  avg_abandon_seconds: number;
  abandon_threshold_minutes: number;
  questions: Array<{
    question_id: number;
    title: string;
    question_type: string;
    total_responses: number;
    correct_responses?: number;
    accuracy_percent?: number;
    options: Array<{ option_id: number; label: string; is_correct?: boolean; count: number }>;
  }>;
}

export interface ControlQuestion {
  id: number;
  tenant_id: number;
  survey_id: number;
  question_type: string;
  title: string;
  description?: string | null;
  media_type?: string | null;
  media_url?: string | null;
  weight?: number;
  required?: number;
  display_format?: string | null;
  min_answers?: number | null;
  max_answers?: number | null;
  order_index?: number | null;
}

export interface ControlQuestionOption {
  id: number;
  tenant_id: number;
  question_id: number;
  label: string;
  value_text?: string | null;
  image_url?: string | null;
  weight?: number;
  is_correct?: number;
  is_other?: number;
  order_index?: number;
}

export interface ControlQuestionLink {
  id: number;
  tenant_id: number;
  survey_id: number;
  token: string;
  status?: string;
  expires_at?: string | null;
  max_responses?: number | null;
  allow_anonymous?: number | null;
  allowed_ips?: string[];
  has_password?: boolean;
  password_preview?: string | null;
  created_at?: string;
  updated_at?: string;
}

@Injectable({ providedIn: 'root' })
export class ControlQuestionService {
  private readonly baseUrl = `${API_BASE_URL}/controlquestion`;

  constructor(private http: HttpClient) {}

  listSurveys(tenantId: number, filters: Record<string, any> = {}): Observable<ControlQuestionSurvey[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });
    return this.http.get<ControlQuestionSurvey[]>(`${this.baseUrl}/surveys`, { params });
  }

  createSurvey(payload: any): Observable<ControlQuestionSurvey> {
    return this.http.post<ControlQuestionSurvey>(`${this.baseUrl}/surveys`, payload);
  }

  getSurvey(tenantId: number, surveyId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<{
      survey: ControlQuestionSurvey;
      questions: ControlQuestion[];
      options: ControlQuestionOption[];
      matrixRows: any[];
      matrixCols: any[];
    }>(`${this.baseUrl}/surveys/${surveyId}`, { params });
  }

  getSurveyMetrics(tenantId: number, surveyId: number): Observable<ControlQuestionSurveyMetrics> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlQuestionSurveyMetrics>(`${this.baseUrl}/surveys/${surveyId}/metrics`, { params });
  }

  updateSurvey(surveyId: number, payload: any): Observable<ControlQuestionSurvey> {
    return this.http.put<ControlQuestionSurvey>(`${this.baseUrl}/surveys/${surveyId}`, payload);
  }

  deleteSurvey(tenantId: number, surveyId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/surveys/${surveyId}`, { params });
  }

  createQuestion(surveyId: number, payload: any): Observable<ControlQuestion> {
    return this.http.post<ControlQuestion>(`${this.baseUrl}/surveys/${surveyId}/questions`, payload);
  }

  updateQuestion(questionId: number, payload: any): Observable<ControlQuestion> {
    return this.http.put<ControlQuestion>(`${this.baseUrl}/questions/${questionId}`, payload);
  }

  deleteQuestion(tenantId: number, questionId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/questions/${questionId}`, { params });
  }

  createOption(questionId: number, payload: any): Observable<ControlQuestionOption> {
    return this.http.post<ControlQuestionOption>(`${this.baseUrl}/questions/${questionId}/options`, payload);
  }

  updateOption(optionId: number, payload: any): Observable<ControlQuestionOption> {
    return this.http.put<ControlQuestionOption>(`${this.baseUrl}/options/${optionId}`, payload);
  }

  deleteOption(tenantId: number, optionId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/options/${optionId}`, { params });
  }

  createMatrixRow(questionId: number, payload: any) {
    return this.http.post(`${this.baseUrl}/questions/${questionId}/matrix-rows`, payload);
  }

  createMatrixCol(questionId: number, payload: any) {
    return this.http.post(`${this.baseUrl}/questions/${questionId}/matrix-cols`, payload);
  }

  updateMatrixRow(rowId: number, payload: any) {
    return this.http.put(`${this.baseUrl}/matrix-rows/${rowId}`, payload);
  }

  updateMatrixCol(colId: number, payload: any) {
    return this.http.put(`${this.baseUrl}/matrix-cols/${colId}`, payload);
  }

  deleteMatrixRow(tenantId: number, rowId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/matrix-rows/${rowId}`, { params });
  }

  deleteMatrixCol(tenantId: number, colId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/matrix-cols/${colId}`, { params });
  }

  createLink(payload: any): Observable<{ id: number; token: string }> {
    return this.http.post<{ id: number; token: string }>(`${this.baseUrl}/links`, payload);
  }

  listLinks(tenantId: number, surveyId: number): Observable<ControlQuestionLink[]> {
    const params = new HttpParams().set('tenant_id', tenantId).set('survey_id', surveyId);
    return this.http.get<ControlQuestionLink[]>(`${this.baseUrl}/links`, { params });
  }

  updateLink(linkId: number, payload: any): Observable<ControlQuestionLink> {
    return this.http.patch<ControlQuestionLink>(`${this.baseUrl}/links/${linkId}`, payload);
  }

  validatePublicLink(token: string, payload: any) {
    return this.http.post(`${this.baseUrl}/public/${token}`, payload);
  }

  submitResponse(token: string, payload: any) {
    return this.http.post(`${this.baseUrl}/public/${token}/response`, payload);
  }

  dispatchSurvey(payload: any) {
    return this.http.post(`${this.baseUrl}/dispatch`, payload);
  }

  listDispatches(tenantId: number, page = 1, pageSize = 10) {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('page', page)
      .set('page_size', pageSize);
    return this.http.get<{ items: any[]; total: number }>(`${this.baseUrl}/dispatches`, { params });
  }

  listRecipientSurveys(tenantId: number, userId: number, status = 'PENDING') {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId)
      .set('status', status);
    return this.http.get<any[]>(`${this.baseUrl}/recipients`, { params });
  }
}
